import { buildLayerHoverFactRows } from "./map-hover-facts.js";
import { buildLandmarkHoverRows } from "./map-hover-landmarks.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import { FISHYMAP_ZONE_CATALOG_READY_EVENT } from "./map-zone-catalog-live.js";
import {
  FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
} from "./map-trade-npc-catalog-live.js";
import { tradeNpcPortraitMarkup } from "./map-trade-npc-presentation.js";
import { normalizeHoverSnapshotValue } from "./map-bridge-output.js";
import { resolveFishGrade } from "../js/components/fish-identity.js";

const HOVER_TOOLTIP_TAG_NAME = "fishymap-hover-tooltip";
const HOVER_REST_SAMPLE_LIMIT = 3;
const HOVER_SAMPLE_ZONE_LIMIT = 3;
const HTMLElementBase = globalThis.HTMLElement ?? class {};
const FISHYMAP_V2_EVENT = "fishymap:v2";

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      (
        {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[char] || char
      ),
  );
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function landmarkAnchorClientPosition(canvas, anchor) {
  if (!canvas || !anchor || typeof canvas.getBoundingClientRect !== "function") {
    return null;
  }
  const bounds = canvas.getBoundingClientRect();
  const logicalWidth = Number(canvas.clientWidth);
  const logicalHeight = Number(canvas.clientHeight);
  const boundsLeft = Number(bounds?.left);
  const boundsTop = Number(bounds?.top);
  const boundsWidth = Number(bounds?.width);
  const boundsHeight = Number(bounds?.height);
  if (
    !Number.isFinite(logicalWidth) ||
    logicalWidth <= 0 ||
    !Number.isFinite(logicalHeight) ||
    logicalHeight <= 0 ||
    !Number.isFinite(boundsLeft) ||
    !Number.isFinite(boundsTop) ||
    !Number.isFinite(boundsWidth) ||
    boundsWidth <= 0 ||
    !Number.isFinite(boundsHeight) ||
    boundsHeight <= 0
  ) {
    return null;
  }
  return {
    x: Math.round(boundsLeft + anchor.x * (boundsWidth / logicalWidth)),
    y: Math.round(boundsTop + anchor.y * (boundsHeight / logicalHeight)),
  };
}

function setBooleanProperty(element, propertyName, value) {
  if (!element) {
    return;
  }
  element[propertyName] = Boolean(value);
}

function setMarkup(element, renderKey, markup) {
  if (!element) {
    return;
  }
  const normalizedKey = String(renderKey ?? "");
  if (element.dataset.renderKey === normalizedKey) {
    return;
  }
  element.dataset.renderKey = normalizedKey;
  element.innerHTML = String(markup ?? "");
}

function spriteIcon(name, sizeClass = "size-4") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${escapeHtml(name)}"></use></svg>`;
}

function pointSampleZoneIndicatorMarkup(zone) {
  if (zone?.zoneKind === "partial") {
    const style = trimString(zone?.swatchRgb)
      ? ` style="--fishymap-layer-fact-rgb:${escapeHtml(zone.swatchRgb)};"`
      : "";
    return `<svg class="fishy-icon size-4 fishymap-point-sample-zone-icon" viewBox="0 0 24 24" aria-hidden="true"${style}><use width="100%" height="100%" href="#fishy-ring-partial"></use></svg>`;
  }
  return trimString(zone?.swatchRgb)
    ? `<span class="fishymap-layer-fact-swatch" style="--fishymap-layer-fact-rgb:${escapeHtml(zone.swatchRgb)};"></span>`
    : "";
}

function overviewRowMarkup(row) {
  const baseIcon = trimString(row?.icon || "information-circle");
  const label = trimString(row?.label);
  const swatchRgb = trimString(row?.swatchRgb);
  const statusIcon = trimString(row?.statusIcon);
  const statusIconTone = trimString(row?.statusIconTone);
  const rowKind = trimString(row?.kind);
  const imageUrl = trimString(row?.imageUrl);
  const badgeImageUrl = trimString(row?.badgeImageUrl);
  const markerImageUrl = trimString(row?.markerImageUrl);
  const characterPortraitState = trimString(row?.characterPortraitState);
  const characterPortraitUrl = trimString(row?.characterPortraitUrl);
  const kindClass = rowKind === "landmark-hover" ? " fishymap-landmark-hover-row" : "";
  const labelClass = label ? "" : " fishymap-overview-row--label-less";
  const dataKind = rowKind ? ` data-hover-row-kind="${escapeHtml(rowKind)}"` : "";
  return `
    <div class="fishymap-overview-row${kindClass}${labelClass}"${dataKind}>
      <span class="fishymap-overview-row-icon" aria-hidden="true">
        ${
          markerImageUrl
            ? `<img class="fishymap-landmark-marker-image" src="${escapeHtml(markerImageUrl)}" alt="" loading="lazy" decoding="async">`
            : characterPortraitUrl
            ? `<img class="block size-10 shrink-0 rounded-lg bg-base-content/10 object-cover object-center" data-character-class-portrait data-character-class-portrait-state="known" src="${escapeHtml(characterPortraitUrl)}" alt="" loading="lazy" decoding="async">`
            : characterPortraitState === "unknown"
            ? `<span class="grid size-10 shrink-0 place-items-center rounded-lg bg-base-content/10" data-character-class-portrait-state="unknown">${spriteIcon("question-mark", "size-6")}</span>`
            : imageUrl
            ? tradeNpcPortraitMarkup({
                imageUrl,
                badgeImageUrl,
                name: row?.value || row?.label || "NPC",
                variant: "hover",
              })
            : swatchRgb
            ? `<span class="fishymap-layer-fact-swatch" style="--fishymap-layer-fact-rgb:${escapeHtml(swatchRgb)};"></span>`
            : spriteIcon(baseIcon, "size-4")
        }
      </span>
      ${label ? `<span class="fishymap-overview-row-label">${escapeHtml(label)}</span>` : ""}
      <span class="fishymap-overview-row-value">
        ${escapeHtml(row?.value || "")}
        ${
          statusIcon
            ? `<span class="fishymap-overview-status ${
                statusIconTone === "subtle" ? "fishymap-overview-status--subtle" : ""
              }" aria-hidden="true">${spriteIcon(statusIcon, "size-4")}</span>`
            : ""
        }
      </span>
    </div>
  `;
}


function itemIconMarkup(row, size = "is-xs") {
  const name = trimString(row?.fishName) || "Unknown fish";
  const gradeTone = resolveFishGrade(row);
  const toneClass = `fishy-item-grade-${escapeHtml(gradeTone)}`;
  const iconUrl = trimString(row?.iconUrl);
  return iconUrl
    ? `<span class="fishy-item-icon-frame ${escapeHtml(size)} ${toneClass}"><img class="fishy-item-icon" src="${escapeHtml(iconUrl)}" alt="${escapeHtml(name)}" crossorigin="anonymous" loading="lazy" decoding="async"></span>`
    : `<span class="fishy-item-icon-frame ${escapeHtml(size)} ${toneClass}"><span class="fishy-item-icon-fallback ${toneClass}">${escapeHtml(name.charAt(0).toUpperCase() || "?")}</span></span>`;
}

function pointSampleZoneMarkup(row) {
  const zones = Array.isArray(row?.zones) ? row.zones : [];
  if (!zones.length) {
    return "";
  }
  const visibleZones = zones.slice(0, HOVER_SAMPLE_ZONE_LIMIT);
  const hiddenZoneCount = Math.max(0, zones.length - visibleZones.length);
  return `
    <div class="fishymap-point-sample-zones">
      <span class="fishymap-point-sample-zone-list">
        ${visibleZones
          .map((zone) => `
            <span class="fishymap-point-sample-zone">
              ${pointSampleZoneIndicatorMarkup(zone)}
              <span class="truncate">${escapeHtml(zone?.name || "")}</span>
            </span>
          `)
          .join("")}
        ${
          hiddenZoneCount
            ? `<span class="fishymap-point-sample-zone fishymap-point-sample-zone-more">+${hiddenZoneCount} zones</span>`
            : ""
        }
      </span>
    </div>
  `;
}

function pointSampleMarkup(row) {
  const name = trimString(row?.fishName) || "Unknown fish";
  const count = Math.max(1, Number.parseInt(row?.sampleCount, 10) || 1);
  const sampleBadge = count > 1 ? `x${count}` : "";
  return `
    <div class="fishymap-point-sample-card" data-zone-kind="${escapeHtml(row?.zoneKind || "")}">
      <div class="fishymap-point-sample-main">
        <span class="fishy-item-row min-w-0">
          ${itemIconMarkup(row, "is-native")}
          <span class="fishymap-point-sample-fish min-w-0">
            <span class="fishymap-point-sample-name truncate">${escapeHtml(name)}</span>
          </span>
        </span>
        ${sampleBadge ? `<span class="badge badge-soft badge-sm">${escapeHtml(sampleBadge)}</span>` : ""}
      </div>
      ${
        trimString(row?.timestampText)
          ? `<div class="fishymap-point-sample-timestamp">${spriteIcon("date-confirmed", "size-4")}<span>${escapeHtml(row.timestampText)}</span></div>`
          : ""
      }
      ${pointSampleZoneMarkup(row)}
    </div>
  `;
}

function pointSampleBadgeText(row) {
  const count = Math.max(1, Number.parseInt(row?.sampleCount, 10) || 1);
  return `x${count}`;
}

function remainingPointRows(rows) {
  const byFish = new Map();
  for (const row of rows) {
    const fishId = Number.parseInt(row?.fishId, 10);
    const itemId = Number.parseInt(row?.itemId, 10);
    const key = [
      Number.isInteger(fishId) ? fishId : "",
      Number.isInteger(itemId) ? itemId : "",
      trimString(row?.fishName),
      trimString(row?.iconUrl),
    ].join(":");
    const current = byFish.get(key);
    const sampleCount = Math.max(1, Number.parseInt(row?.sampleCount, 10) || 1);
    if (current) {
      current.sampleCount += sampleCount;
    } else {
      byFish.set(key, { ...row, sampleCount });
    }
  }
  const sortedRows = [...byFish.values()].sort(
    (left, right) =>
      Math.max(1, Number.parseInt(right?.sampleCount, 10) || 1) -
        Math.max(1, Number.parseInt(left?.sampleCount, 10) || 1) ||
      trimString(left?.fishName).localeCompare(trimString(right?.fishName)),
  );
  const visibleRows = sortedRows.slice(0, HOVER_REST_SAMPLE_LIMIT);
  const hiddenSampleCount = sortedRows
    .slice(HOVER_REST_SAMPLE_LIMIT)
    .reduce((total, row) => total + Math.max(1, Number.parseInt(row?.sampleCount, 10) || 1), 0);
  return { hiddenSampleCount, visibleRows };
}

function remainingPointSampleMarkup(row) {
  const name = trimString(row?.fishName) || "Unknown fish";
  const badge = pointSampleBadgeText(row);
  return `
    <span class="fishymap-point-sample-rest-item">
      ${itemIconMarkup(row, "is-hover-rest")}
      ${badge ? `<span class="fishymap-point-sample-rest-count">${escapeHtml(badge)}</span>` : ""}
    </span>
  `;
}

function pointSampleGroupMarkup(pointRows) {
  if (!pointRows.length) {
    return "";
  }
  const [topRow, ...remainingRows] = pointRows;
  const { hiddenSampleCount, visibleRows: restRows } = remainingPointRows(remainingRows);
  return `
    <div class="fishymap-point-sample-group">
      ${pointSampleMarkup(topRow)}
      ${
        restRows.length
          ? `<div class="fishymap-point-sample-rest">${restRows.map((row) => remainingPointSampleMarkup(row)).join("")}${
              hiddenSampleCount > 0
                ? `<span class="fishymap-point-sample-rest-more">+${escapeHtml(hiddenSampleCount)}</span>`
                : ""
            }</div>`
          : ""
      }
    </div>
  `;
}

function landmarkHoverInfoMarkup(landmarkRows) {
  const pointRows = landmarkRows.filter((row) => row?.kind === "point-sample");
  const targetRows = landmarkRows.filter((row) => row?.kind !== "point-sample");
  return [
    pointSampleGroupMarkup(pointRows),
    targetRows.length
      ? `<div class="fishymap-landmark-hover-list">${targetRows.map((row) => overviewRowMarkup(row)).join("")}</div>`
      : "",
  ].join("");
}

function buildStateBundle(projection) {
  return {
    state: {
      catalog: {
        layers: Array.isArray(projection?.runtime?.catalog?.layers)
          ? cloneJson(projection.runtime.catalog.layers)
          : [],
        fish: Array.isArray(projection?.runtime?.catalog?.fish)
          ? cloneJson(projection.runtime.catalog.fish)
          : [],
        items: Array.isArray(projection?.runtime?.catalog?.items)
          ? cloneJson(projection.runtime.catalog.items)
          : [],
      },
    },
    inputState: {
      filters: isPlainObject(projection?.bridged?.filters)
        ? cloneJson(projection.bridged.filters)
        : {},
    },
  };
}

function normalizeHoverEventDetail(detail) {
  return normalizeHoverSnapshotValue(detail);
}

function ensureHoverTooltipMarkup(host, documentRef = globalThis.document) {
  const existingLayers = host.querySelector?.("#fishymap-hover-layers");
  const existingInfo = host.querySelector?.("#fishymap-hover-info");
  if (existingLayers && existingInfo) {
    return {
      hoverLayers: existingLayers,
      hoverInfo: existingInfo,
    };
  }
  if (documentRef && typeof documentRef.createElement === "function") {
    const layers = documentRef.createElement("div");
    layers.id = "fishymap-hover-layers";
    layers.hidden = true;
    const info = documentRef.createElement("div");
    info.id = "fishymap-hover-info";
    info.hidden = true;
    host.replaceChildren?.(layers, info);
    return {
      hoverLayers: layers,
      hoverInfo: info,
    };
  }
  host.innerHTML = '<div id="fishymap-hover-layers" hidden></div><div id="fishymap-hover-info" hidden></div>';
  return {
    hoverLayers: host.querySelector?.("#fishymap-hover-layers") || null,
    hoverInfo: host.querySelector?.("#fishymap-hover-info") || null,
  };
}

export class FishyMapHoverTooltipElement extends HTMLElementBase {
  constructor() {
    super();
    this._shell = null;
    this._mapShellProjection = null;
    this._connected = false;
    this._canvas = null;
    this._rafId = 0;
    this._pointerRafId = 0;
    this._zoneCatalog = [];
    this._tradeNpcCatalog = null;
    this._elements = null;
    this._state = {
      pointerActive: false,
      pointerX: 0,
      pointerY: 0,
      pointerDirty: false,
      hover: null,
    };
    this._handleCanvasPointerMove = (event) => {
      this._state.pointerActive = true;
      this.setPointerPosition(event?.clientX ?? 0, event?.clientY ?? 0);
      this.schedulePointerPositionWrite();
      if (this.hidden) {
        this.scheduleRender();
      }
    };
    this._handleCanvasPointerLeave = () => {
      this._state.pointerActive = false;
      this._state.pointerDirty = false;
      if (this._pointerRafId && typeof globalThis.cancelAnimationFrame === "function") {
        globalThis.cancelAnimationFrame(this._pointerRafId);
      }
      this._pointerRafId = 0;
      setBooleanProperty(this._elements?.hoverLayers, "hidden", true);
      setBooleanProperty(this._elements?.hoverInfo, "hidden", true);
      setBooleanProperty(this, "hidden", true);
    };
    this._handleHoverChanged = (event) => {
      this._state.hover = normalizeHoverEventDetail(event?.detail);
      this._state.pointerDirty = true;
      this.scheduleRender();
    };
    this._handleV2EventBatch = (event) => {
      const events = Array.isArray(event?.detail?.events) ? event.detail.events : [];
      for (const entry of events) {
        if (entry?.type === "hover-changed") {
          this._handleHoverChanged({ detail: entry });
        }
      }
    };
    this._handleZoneCatalogReady = (event) => {
      this._zoneCatalog = Array.isArray(event?.detail?.zoneCatalog)
        ? cloneJson(event.detail.zoneCatalog)
        : [];
      this.scheduleRender();
    };
    this._handleTradeNpcCatalogChanged = (event) => {
      this._tradeNpcCatalog = isPlainObject(event?.detail?.catalog)
        ? cloneJson(event.detail.catalog)
        : null;
      this.scheduleRender();
    };
  }

  connectedCallback() {
    this._connected = true;
    this._shell = this.closest?.("#map-page-shell") || null;
    this._tradeNpcCatalog = isPlainObject(this._shell?.fishyMapTradeNpcCatalog)
      ? cloneJson(this._shell.fishyMapTradeNpcCatalog)
      : null;
    this._canvas = this._shell?.querySelector?.("#bevy") || globalThis.document?.getElementById?.("bevy") || null;
    this._elements = ensureHoverTooltipMarkup(this, globalThis.document);
    this._canvas?.addEventListener?.("pointermove", this._handleCanvasPointerMove);
    this._canvas?.addEventListener?.("pointerleave", this._handleCanvasPointerLeave);
    this._shell?.addEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.addEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.addEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    this.render();
  }

  disconnectedCallback() {
    this._connected = false;
    this._canvas?.removeEventListener?.("pointermove", this._handleCanvasPointerMove);
    this._canvas?.removeEventListener?.("pointerleave", this._handleCanvasPointerLeave);
    this._shell?.removeEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.removeEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.removeEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    if (this._rafId && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this._rafId);
    }
    if (this._pointerRafId && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this._pointerRafId);
    }
    this._rafId = 0;
    this._pointerRafId = 0;
    this._shell = null;
    this._canvas = null;
    this._elements = null;
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  set mapShellProjection(value) {
    const projection = assertMapShellProjectionV1(value);
    const previousProjection = this._mapShellProjection;
    if (previousProjection) {
      if (projection.revision < previousProjection.revision) {
        throw new RangeError("Map shell projection revision cannot regress");
      }
      if (projection.revision === previousProjection.revision) {
        return;
      }
    }
    this._mapShellProjection = projection;
    if (
      this._connected
      && (
        !previousProjection
        || projection.ui !== previousProjection.ui
        || projection.bridged !== previousProjection.bridged
        || projection.runtime !== previousProjection.runtime
        || projection.characterCatalog !== previousProjection.characterCatalog
      )
    ) {
      this.scheduleRender();
    }
  }

  setPointerPosition(clientX, clientY) {
    const pointerX = Math.round(clientX);
    const pointerY = Math.round(clientY);
    if (this._state.pointerX === pointerX && this._state.pointerY === pointerY) {
      return;
    }
    this._state.pointerX = pointerX;
    this._state.pointerY = pointerY;
    this._state.pointerDirty = true;
  }

  writePointerPosition() {
    if (!this._state.pointerDirty) {
      return;
    }
    this._state.pointerDirty = false;
    const x = this._state.pointerX;
    const y = this._state.pointerY;
    const landmarkAnchor = landmarkAnchorClientPosition(
      this._canvas,
      this._state.hover?.landmarkViewportAnchor,
    );
    const infoX = landmarkAnchor?.x ?? x;
    const infoY = landmarkAnchor?.y ?? y;
    if (this._elements?.hoverLayers?.style) {
      this._elements.hoverLayers.style.transform = `translate3d(${x + 18}px, ${y + 22}px, 0)`;
    }
    if (this._elements?.hoverInfo?.style) {
      this._elements.hoverInfo.style.transform = `translate3d(calc(${infoX}px - 50%), calc(${infoY}px - 100% - 18px), 0)`;
    }
  }

  schedulePointerPositionWrite() {
    if (this._pointerRafId) {
      return;
    }
    if (typeof globalThis.requestAnimationFrame === "function") {
      this._pointerRafId = globalThis.requestAnimationFrame(() => {
        this._pointerRafId = 0;
        if (this._state.pointerActive && !this.hidden) {
          this.writePointerPosition();
        }
      }) || 0;
      if (this._pointerRafId) {
        return;
      }
    }
    if (this._state.pointerActive && !this.hidden) {
      this.writePointerPosition();
    }
  }

  render() {
    this._rafId = 0;
    const projection = this._mapShellProjection;
    const stateBundle = buildStateBundle(projection);
    const landmarkRows = buildLandmarkHoverRows({
      hover: this._state.hover,
      characterCatalog: projection?.characterCatalog,
      stateBundle,
      pointSamplesEnabled:
        projection?.ui?.layers?.sampleHoverVisibleByLayer?.fish_evidence !== false,
      tradeNpcCatalog: this._tradeNpcCatalog,
      zoneCatalog: this._zoneCatalog,
    });
    const factRows = buildLayerHoverFactRows({
      hover: this._state.hover,
      stateBundle,
      visibilityByLayer: projection?.ui?.layers?.hoverFactsVisibleByLayer || {},
      zoneCatalog: this._zoneCatalog,
    });
    if (!this._state.pointerActive || (landmarkRows.length === 0 && factRows.length === 0)) {
      setMarkup(this._elements?.hoverLayers, "[]", "");
      setMarkup(this._elements?.hoverInfo, "[]", "");
      setBooleanProperty(this._elements?.hoverLayers, "hidden", true);
      setBooleanProperty(this._elements?.hoverInfo, "hidden", true);
      setBooleanProperty(this, "hidden", true);
      delete this.dataset.landmarkHover;
      delete this.dataset.pointSamples;
      return;
    }
    const pointRows = landmarkRows.filter((row) => row?.kind === "point-sample");
    if (landmarkRows.length) {
      this.dataset.landmarkHover = "true";
    } else {
      delete this.dataset.landmarkHover;
    }
    if (pointRows.length) {
      this.dataset.pointSamples = "true";
    } else {
      delete this.dataset.pointSamples;
    }
    this.writePointerPosition();
    setMarkup(
      this._elements?.hoverLayers,
      JSON.stringify(factRows.map((row) => [row.layerId, row.key, row.value])),
      factRows.map((row) => overviewRowMarkup(row)).join(""),
    );
    setMarkup(
      this._elements?.hoverInfo,
      JSON.stringify(landmarkRows.map((row) => [
        row.kind,
        row.key,
        row.value || row.fishName || "",
        row.imageUrl || "",
        row.markerImageUrl || "",
        row.characterPortraitState || "",
        row.characterClassName || "",
        row.characterPortraitUrl || "",
        row.icon || "",
        row.statusIcon || "",
      ])),
      landmarkHoverInfoMarkup(landmarkRows),
    );
    setBooleanProperty(this._elements?.hoverLayers, "hidden", factRows.length === 0);
    setBooleanProperty(this._elements?.hoverInfo, "hidden", landmarkRows.length === 0);
    setBooleanProperty(this, "hidden", false);
  }

  scheduleRender() {
    if (this._rafId) {
      return;
    }
    if (typeof globalThis.requestAnimationFrame === "function") {
      this._rafId = globalThis.requestAnimationFrame(() => {
        this.render();
      }) || 0;
      if (this._rafId) {
        return;
      }
    }
    this.render();
  }
}

export function registerFishyMapHoverTooltipElement(registry = globalThis.customElements) {
  if (!registry || typeof registry.get !== "function" || typeof registry.define !== "function") {
    return false;
  }
  if (registry.get(HOVER_TOOLTIP_TAG_NAME)) {
    return true;
  }
  registry.define(HOVER_TOOLTIP_TAG_NAME, FishyMapHoverTooltipElement);
  return true;
}

registerFishyMapHoverTooltipElement();
